/**
 * Iter-109 — Theme toggle pill.
 *
 * Two-segment radio control (Dark / Light) backed by the shared
 * `useTheme()` hook. Used in:
 *   • ProjectSettings → System block (the canonical surface)
 *   • MyProjects header menu (top-level shortcut for users who haven't
 *     opened a project yet)
 *
 * Visuals match the existing pill / segmented controls in the app
 * (border, primary fill on active, instant-tap so the swap paints in
 * one frame on iOS Safari) so the new affordance reads as native to
 * the design language.
 */

import { Sun, Moon } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

export default function ThemeToggle({ testid = "theme-toggle", label = true }) {
    const { theme, setTheme } = useTheme();

    return (
        <div className="flex items-center gap-3">
            {label ? (
                <span className="text-xs uppercase tracking-[0.25em] text-neutral-500">
                    Appearance
                </span>
            ) : null}
            <div
                role="radiogroup"
                aria-label="Theme"
                data-testid={testid}
                className="inline-flex rounded-full border border-white/10 bg-secondary p-0.5"
            >
                <button
                    type="button"
                    role="radio"
                    aria-checked={theme === "dark"}
                    data-active={theme === "dark"}
                    data-testid={`${testid}-dark`}
                    onClick={() => setTheme("dark")}
                    className="instant-tap segmented-button segmented-pill rounded-full px-3 h-8 text-xs font-semibold flex items-center gap-1.5"
                >
                    <Moon size={13} />
                    Dark
                </button>
                <button
                    type="button"
                    role="radio"
                    aria-checked={theme === "light"}
                    data-active={theme === "light"}
                    data-testid={`${testid}-light`}
                    onClick={() => setTheme("light")}
                    className="instant-tap segmented-button segmented-pill rounded-full px-3 h-8 text-xs font-semibold flex items-center gap-1.5"
                >
                    <Sun size={13} />
                    Light
                </button>
            </div>
        </div>
    );
}
